import type { ScriptShot } from '@/api/capture';
import type { CaptureSession, ShotHandle } from '@/capture/captureSession';
import { type ClipVerdict, assessClip, cameraStoppedVerdict, useClipSampler } from '@/capture/clipQuality';
import { errorCue, stepCue } from '@/capture/guidance/guidanceCopy';
import { guidancePlayer, setGuidanceRecording, useGuidance, useGuidanceCue } from '@/capture/guidance/guidanceStore';
import { hapticGoodShot, hapticRetake } from '@/capture/haptics';
import { track } from '@/flow/instrument';
import { motionPermissionNeedsGesture, requestMotionPermission } from '@/capture/imu';
import { useLowData } from '@/capture/lowData';
import type { GeoFix } from '@/capture/shotGeo';
import { parseStamp, previewModel } from '@/capture/stampPreview';
import { useFramingHints } from '@/capture/useFramingHints';
import { useShotGeoWatch } from '@/capture/GeoWatchContext';
import { useLatestFix } from '@/capture/useLatestFix';
import { useNow } from '@/capture/useNow';
import { useShotRecorder } from '@/capture/useShotRecorder';
import { useStampMosaic, useStampTile } from '@/capture/useStampTile';
import { useT } from '@/i18n/runtime';
import { shotText } from '@/i18n/serverText';
import { useShotCamera } from '@/media/CaptureCameraContext';
import type { CaptureFacing } from '@/media/useCaptureCamera';
import { useCaptureIntegrity } from '@/media/useCaptureIntegrity';
import type { GeoPermissionReport } from '@/media/useGeoEvidence';
import { useCaptureStore } from '@/store/captureStore';
import type { RefusedShot } from '@/store/captureStore';
import { defaultFacing } from '@/store/selectors';
import type { TenantConfig } from '@/theme/tenant';
import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { ShotHeader, refusalKeys } from './ShotHeader';
import { useDemoAutoPlay } from './demos/demoStore';
import { Affirmation, useAffirmation } from './components/Affirmation';
import { AppBar } from './components/AppBar';
import { BlockedCameraCard, blockedCameraWords } from './components/BlockedCameraCard';
import { Button } from './components/Button';
import { BarButton, BarSlot, CaptureBar, Shutter } from './components/CaptureBar';
import { ChallengeOverlay } from './components/ChallengeOverlay';
import { FramingHintLine } from './components/FramingHint';
import { Icon } from './components/Icon';
import { distanceWordsFor } from './components/LocationCard';
import { SavedWillSend } from './components/SavedWillSend';
import { StampMapSheet } from './components/StampMapSheet';
import { CameraStalled } from './components/CameraStalled';
import { StampPreviewCard } from './components/StampPreviewCard';
import { useHelp, useHelpStep } from './help/HelpContext';

export interface ShotProps {
  tenant: TenantConfig;
  session: CaptureSession;
  shot: ScriptShot;
  stepIndex: number;
  stepTotal: number;
  refusal: RefusedShot | null;
  recaptureOnly: boolean;
  geoPermission: GeoPermissionReport;
}

type Phase = 'idle' | 'recording' | 'judging' | 'rejected' | 'sealing' | 'sealed';

const RP_NAME = import.meta.env.VITE_RP_NAME || 'Site verification';

/**
 * FSD-20 D15: the facing badge names only what the camera REPORTED. Unknown is its own state — most desktop webcams
 * report no `facingMode`, and "Rear camera" on a laptop was simply wrong.
 */
export function cameraBadgeKey(active: CaptureFacing | null): string {
  return active === 'user' ? 'sv.capture.camera.badge.front' : active === 'environment' ? 'sv.capture.camera.badge.rear' : 'sv.capture.camera.badge.camera';
}

/**
 * A CLIP shot: 480p-first stream (+ mic), MediaRecorder with a 2 s timeslice, every chunk hashed
 * into the per-shot chain and queued for background upload WHILE recording, the IMU window
 * summarised per chunk, the live quality cue on the viewfinder, the verdict at stop, the challenge
 * overlay when the script asks. A rejected clip is abandoned (its queued chunks dropped) and the
 * representative records again; a NOT_EVALUATED verdict may be used with the honest label.
 *
 * Layout (SPEC_CAPTURE_UI_20260925): the viewfinder fills the screen; instruction card, progress
 * strip and the challenge card over its top; the location card and the live quality cue over its
 * bottom; the record / stop shutter and the decision pills in the one bottom bar. No grid or level
 * here (§D: clips carry the challenge overlay and the live cue instead).
 */
export function ShotClip(p: ShotProps): JSX.Element {
  const { t, locale } = useT();
  const { session, shot } = p;
  // G1 (RCA 2026-11-03): the flow's ONE camera session — this screen re-mounts per shot, the camera does not.
  const cam = useShotCamera();
  const rec = useShotRecorder();
  const [phase, setPhase] = useState<Phase>('idle');
  const [verdict, setVerdict] = useState<ClipVerdict | null>(null);
  const [facing, setFacing] = useState<CaptureFacing>(defaultFacing(shot.promptKey));
  const [motionAsked, setMotionAsked] = useState(false);
  // FSD-20 Addendum C: the motion sentence is said only while iOS's own "motion and orientation" prompt is up (the
  // first record tap) — it no longer spends a line of the bar on every clip, on phones that never ask.
  const [motionNote, setMotionNote] = useState(false);
  // The fix the in-flight clip's chunks bind (`handle.boundFix`) — the card freezes to it from start to seal / retake.
  const [boundFix, setBoundFix] = useState<GeoFix | null>(null);
  const handleRef = useRef<ShotHandle | null>(null);
  const lastChunkT = useRef(0);
  const disposed = useRef(false);
  // A4 (US-03482 round 3): the camera stopped under the take in flight — it ends as a retake, nothing sealed
  const cameraStopped = useRef(false);
  const camPhase = useRef(cam.phase);
  camPhase.current = cam.phase;
  const sampler = useClipSampler(cam.getVideo, phase === 'recording');
  const fix = useLatestFix();
  // G2 (RCA 2026-11-03): the capture's ONE shared watch; the card freezes to the shot's bound fix while it is in flight.
  const watch = useShotGeoWatch();
  // The exchange's `summary.stamp` (contract v0.1.12) — null on an older be ⇒ the no-anchor card form.
  const summary = useCaptureStore((s) => s.summary);
  const stamp = parseStamp(summary?.stamp);
  const now = useNow();
  const cardModel = previewModel(fix, p.geoPermission, stamp, now, boundFix, watch.error);
  // Low-data mode (SPEC_CAPTURE_UX_R2 §7.4): the thumbnail waits for a "Show map" tap.
  const lowData = useLowData();
  const [mapAsked, setMapAsked] = useState(false);
  const tileWanted = cardModel.kind === 'fix' && stamp !== null && stamp.liveCard === 'COORDINATES';
  // The map thumbnail under the fix the card shows — frozen with the card while the clip records (the bound fix).
  const tile = useStampTile(cardModel.kind === 'fix' ? { lat: cardModel.lat, lng: cardModel.lng } : null, tileWanted && (!lowData.on || mapAsked), stamp?.anchor ?? null);
  const [mapOpen, setMapOpen] = useState(false);
  // FSD-20 D7/D8: the location card starts as one row so the challenge card is never covered; "Details" opens the full
  // lines before recording, and recording always folds them back (the frame must stay clear).
  const [locOpen, setLocOpen] = useState(false);
  // …and they STAY folded once the clip leaves idle: a rejected take comes back to a one-row card, not the opened one.
  useEffect(() => {
    if (phase !== 'idle') setLocOpen(false);
  }, [phase]);
  // Round 2: help layer, framing hints while lining up (the live cue takes over once recording), affirmation, haptics.
  const help = useHelp();
  const framing = useFramingHints(cam.getVideo, cam.phase === 'live' && phase === 'idle', false);
  const affirm = useAffirmation();
  const guidance = useGuidance();
  const mosaic = useStampMosaic(mapOpen && tile.status === 'ready' ? tile.ref : null, stamp?.anchor ?? null);
  const permissions = useCallback(
    () => ({ camera: cam.phase === 'live', microphone: cam.hasAudio, geolocation: p.geoPermission, motion: session.imu.permission }),
    [cam.phase, cam.hasAudio, p.geoPermission, session.imu.permission],
  );
  useCaptureIntegrity(cam.getStream, cam.phase === 'live', permissions);

  useEffect(() => {
    disposed.current = false;
    return () => {
      disposed.current = true;
    };
  }, []);

  useEffect(() => {
    if (cam.phase === 'primer' && cam.preGranted) cam.start(facing, { profile: 'clip', audio: true });
  }, [cam.phase, cam.preGranted, cam.start, facing]);

  // G1: a shot that mounts onto the flow's running session asks for ITS profile and lens — a no-op, a quality switch
  // on the running track, or (another lens / the mic still missing) the one re-acquisition that is really needed.
  const camRunning = cam.phase === 'live' || cam.phase === 'stalled';
  useEffect(() => {
    if (camRunning) cam.start(facing, { profile: 'clip', audio: true });
  }, [camRunning, cam.start, facing]);

  /**
   * THE rule that protects the evidence (SPEC_AUDIO_GUIDANCE §3.2): guidance never plays while a clip is
   * recording. The phone's speaker would print the prompt into the evidence audio track and pollute a
   * SPEAK_NONCE challenge. The interlock is the player's, set from the recorder's own phase — the in-clip
   * instruction stays text (the instruction card).
   */
  // begin() sets the interlock synchronously; this only RELEASES it once the clip is no longer recording.
  useEffect(() => {
    if (phase !== 'recording') setGuidanceRecording(false);
    return () => setGuidanceRecording(false);
  }, [phase]);

  // A4 (US-03482 round 3, F-7 / P-1): the camera is never restarted under a recording clip (begin() sets this
  // synchronously; it is released here). A camera that stops under one — no new frame for ~10 s, muted, or ended — ends
  // the take as a retake: the recorder stops, onStop abandons the take, and the stalled card offers Restart camera.
  const { setRecording } = cam;
  useEffect(() => {
    setRecording(phase === 'recording');
  }, [phase, setRecording]);
  useEffect(() => () => setRecording(false), [setRecording]);
  const stopRecorder = rec.stop;
  useEffect(() => {
    if (phase !== 'recording' || cam.phase !== 'stalled' || cameraStopped.current) return;
    cameraStopped.current = true;
    stopRecorder();
  }, [phase, cam.phase, stopRecorder]);

  const flip = (): void => {
    const next: CaptureFacing = facing === 'user' ? 'environment' : 'user';
    setFacing(next);
    cam.start(next, { profile: 'clip', audio: true });
  };

  /** The unlock gesture (§3.1): the one element is created inside this handler, never outside a user gesture. */
  const turnOn = (): void => {
    guidance.unlock();
    cam.start(facing, { profile: 'clip', audio: true });
  };

  const begin = async (): Promise<void> => {
    const stream = cam.getStream();
    if (!stream || phase === 'recording') return;
    // The evidence interlock, SYNCHRONOUSLY in the tap and before any await: guidance stops now and stays silent until
    // the clip is decided — never a cue that starts in the gap before rec.start() and prints onto the audio track.
    setGuidanceRecording(true);
    guidancePlayer().cancel();
    if (!motionAsked) {
      setMotionAsked(true);
      if (motionPermissionNeedsGesture()) setMotionNote(true);
      session.imu.permission = await requestMotionPermission();
      setMotionNote(false);
      if (session.imu.permission === 'granted' || session.imu.permission === 'not-required') session.imu.start();
    }
    await session.ensureSigningKey(RP_NAME);
    if (disposed.current) return;
    const handle = await session.beginShot(shot);
    if (disposed.current) return;
    if (!cam.getStream()) {
      // the camera went away during the awaits: nothing records, so the interlock is released
      void handle.abandon();
      setGuidanceRecording(false);
      return;
    }
    handleRef.current = handle;
    setBoundFix(handle.boundFix);
    sampler.reset();
    setVerdict(null);
    lastChunkT.current = Date.now();
    cameraStopped.current = false;
    cam.setRecording(true);
    setPhase('recording');
    rec.start(stream, {
      maxDurationS: shot.maxDurationS > 0 ? shot.maxDurationS : 60,
      onChunk: (c) => {
        const from = lastChunkT.current;
        const to = Date.now();
        lastChunkT.current = to;
        void handle.appendChunk(c.blob, c.capturedAt, { from, to });
      },
      onStop: (info) => {
        if (disposed.current) return;
        setPhase('judging');
        // Let the last dataavailable land before judging (MediaRecorder fires it before stop).
        window.setTimeout(() => {
          if (disposed.current) return;
          // G4: the bytes are read again here — the last dataavailable can land after onstop on some browsers.
          const measured = assessClip(sampler.samples(), info.durationS, shot, Math.max(info.totalBytes, rec.bytesRecorded()));
          // A4: a take the camera stopped under is never judged nor sealed — its queued chunks are dropped, Re-record
          // records the step again (once the camera is back: the stalled card's Restart camera)
          const track = cam.getStream()?.getVideoTracks()[0];
          if (cameraStopped.current || camPhase.current === 'stalled' || track?.readyState === 'ended') {
            cameraStopped.current = true;
            void handle.abandon();
            handleRef.current = null;
            setBoundFix(null);
            setVerdict(cameraStoppedVerdict(measured));
            setPhase('rejected');
            return;
          }
          const v = measured;
          if (v.reason === 'EMPTY') {
            // never sealed: nothing usable was recorded — its queued chunks are dropped now; Retake records again
            void handle.abandon();
            handleRef.current = null;
            cam.recheckFrames(); // an empty take is a camera to doubt: the watchdog looks again
          }
          setVerdict(v);
          if (v.ok) {
            affirm.trigger();
            hapticGoodShot();
            void seal(handle, info.mimeType, info.durationS);
          } else setPhase('rejected');
        }, 50);
      },
      onError: () => {
        if (disposed.current) return;
        void handle.abandon();
        setBoundFix(null);
        setPhase('idle');
      },
    });
  };

  const seal = async (handle: ShotHandle, mimeType: string, durationS: number): Promise<void> => {
    setPhase('sealing');
    // appendChunk calls are serialised inside the chain; wait for the queue to settle.
    await new Promise((r) => setTimeout(r, 0));
    await handle.seal({ mimeType, durationS });
    if (!disposed.current) setPhase('sealed');
  };

  const useAnyway = (): void => {
    const handle = handleRef.current;
    if (!handle || !verdict || !verdict.mayProceedUnevaluated) return;
    void seal(handle, rec.mimeType ?? 'video/webm', verdict.metrics.durationS);
  };

  const retake = (): void => {
    const handle = handleRef.current;
    if (handle) void handle.abandon();
    handleRef.current = null;
    setBoundFix(null);
    rec.reset();
    setVerdict(null);
    cameraStopped.current = false;
    setPhase('idle');
    cam.recheckFrames();
    hapticRetake();
  };

  const camBlocked = ['denied', 'os-denied', 'no-device', 'webview', 'unsupported'].includes(cam.phase) || rec.phase === 'unsupported';
  useHelpStep({
    screen: 'capture',
    shot,
    optional: !shot.mandatory,
    onSkip: (reason) => {
      useCaptureStore.getState().markSkipped(shot.shotId, reason);
      session.skipShot(shot);
    },
    tenantName: p.tenant.name,
    blocked: camBlocked ? 'camera' : p.geoPermission === 'DENIED' ? 'location' : null,
  });
  const live = cam.phase === 'live';
  const recording = phase === 'recording';
  // §3.3 / §3.1 — the error card's own words, else the server's prompt verbatim; the demonstration on a first
  // encounter of this shot type, never while the clip is running.
  const blockedWords = blockedCameraWords(t, cam.phase, cam.detail);
  useGuidanceCue(
    camBlocked
      ? errorCue('camera', [blockedWords.title, blockedWords.body], locale)
      : p.refusal
        ? errorCue(`refused.${p.refusal.code}`, [t(refusalKeys(p.refusal.code, shot).title), t(refusalKeys(p.refusal.code, shot).body)], locale)
        : stepCue(shot, locale),
  );
  useDemoAutoPlay(shot, !camBlocked && phase === 'idle');
  const elapsedS = Math.floor(rec.elapsedMs / 1000);
  const hint = ((): { text: string; alert: boolean } => {
    // A4: "the camera stopped" is said politely (the stalled card beside it says how to get the camera back)
    if (phase === 'rejected' && verdict?.messageKey) return { text: t(verdict.messageKey), alert: verdict.reason !== 'CAMERA_STOPPED' };
    if (phase === 'judging') return { text: t('sv.capture.assessing'), alert: false };
    if (phase === 'sealing' || phase === 'sealed') return { text: t('sv.capture.queued'), alert: false };
    if (recording) {
      if (sampler.cue === 'too-dark') return { text: t('sv.capture.hint.dark'), alert: false };
      if (sampler.cue === 'too-bright') return { text: t('sv.capture.hint.bright'), alert: false };
      if (sampler.cue === 'blurry') return { text: t('sv.capture.hint.blurry'), alert: false };
      return { text: t('sv.capture.recording'), alert: false };
    }
    switch (cam.phase) {
      case 'checking': return { text: t('sv.capture.camera.checking'), alert: false };
      case 'primer': return { text: t('sv.capture.camera.primer'), alert: false };
      case 'starting': return { text: t('sv.capture.camera.starting'), alert: false };
      case 'stalled': return { text: '', alert: false }; // G3: the CameraStalled card speaks
      // FSD-20 D6: a framing correction ("Hold still") IS the line-up advice, said precisely — the generic sentence
      // steps aside for it so the bottom stack spends one row, not two.
      case 'live': return { text: framing ? '' : t('sv.capture.hint.lineup'), alert: false };
      default: return { text: '', alert: false };
    }
  })();
  // FSD-20 D15: unknown facing (a desktop webcam) mirrors when the front camera is what was asked for
  const mirror = (cam.activeFacing ?? cam.requestedFacing) === 'user';
  const videoStyle: CSSProperties = { objectFit: 'cover', ...(mirror ? { transform: 'scaleX(-1)' } : {}) };

  const pills = (
    <div className="sv-pills">
      {recording ? (
        <span className="sv-pill is-rec mono" data-testid="rec-timer">● {t('sv.capture.elapsed', { s: String(elapsedS) })}</span>
      ) : null}
      <span className="sv-pill" data-testid="camera-badge">
        <Icon name="camera" size={12} />
        {t(cameraBadgeKey(cam.activeFacing))}
      </span>
    </div>
  );
  const decide = camBlocked || cam.phase === 'primer' || phase === 'rejected' || phase === 'judging' || phase === 'sealing' || phase === 'sealed';
  // Skip shows exactly where the bar's Skip used to: camera live, nothing to decide, not recording (FSD-20 Addendum C)
  const skip = !decide && !recording && !shot.mandatory ? () => session.skipShot(shot) : null;
  const header = <ShotHeader shot={shot} stepIndex={p.stepIndex} stepTotal={p.stepTotal} refusal={p.refusal} recaptureOnly={p.recaptureOnly} right={pills} onSkip={skip} folded={recording} />;
  const challenge = (
    <>
      {shot.challenge ? <ChallengeOverlay challenge={shot.challenge} texts={shot.promptTexts} nonce={session.nonce} live={recording} shownAbove={shotText(shot, locale).text} /> : null}
      {shot.challenge?.type === 'SPEAK_NONCE' && live && !cam.hasAudio ? (
        <div className="consent-note mono sv-instruct-alert" role="alert" data-testid="mic-off">{t('sv.capture.micOff')}</div>
      ) : null}
    </>
  );
  // FSD-20 Addendum C: shooting, the bar carries no sentence — the camera gets the height. The decide states keep the
  // footer; "Saved, will send" sits over the camera with the other status lines (in the bar only when blocked).
  const caption = decide ? (
    <>
      {camBlocked ? <SavedWillSend /> : null}
      <span>{t('sv.capture.footer')}</span>
    </>
  ) : null;

  return (
    <div className="app-view sv-cam" data-testid="clip-screen">
      <AppBar tenant={p.tenant} subtitle={t('sv.subtitle')} />

      {camBlocked ? (
        <div className="sv-cam-blocked">
          {header}
          {challenge}
          {rec.phase === 'unsupported' ? (
            <div className="card pad" style={{ textAlign: 'center' }} data-testid="blocked-camera">
              <div className="h-title sm">{t('sv.cam.unsupported.title')}</div>
              <p className="sub-sm">{t('sv.cam.unsupported.body')}</p>
            </div>
          ) : (
            <BlockedCameraCard cam={cam} />
          )}
        </div>
      ) : (
        <div className="viewport sv-vf" data-testid="clip-viewport">
          {cam.phase === 'starting' || cam.phase === 'live' || cam.phase === 'stalled' ? (
            <video ref={cam.attachVideo} className="vp-scene" autoPlay playsInline muted style={videoStyle} />
          ) : null}
          <div className="sv-vf-top">
            {header}
            {challenge}
          </div>
          {/* FSD-20 D5: the clear middle — the affirmation lands here, never on the instruction or the challenge */}
          <div className="sv-vf-mid">
            <Affirmation show={affirm.show} />
          </div>
          <div className="sv-vf-bottom">
            <SavedWillSend />
            {motionNote ? (
              <div className="vp-hint" role="status" data-testid="motion-primer">{t('sv.capture.motion.primer')}</div>
            ) : null}
            <StampPreviewCard
              model={cardModel}
              liveCard={stamp?.liveCard ?? 'COORDINATES'}
              compact={recording || !locOpen}
              onToggle={recording ? null : () => setLocOpen((o) => !o)}
              tenantName={p.tenant.name}
              tile={tile}
              onExpand={() => {
                // §10 "Map tap-to-expand": the expand rate is what says whether the map earns its screen space.
                track('map.expand', { kind: 'CLIP' });
                setMapOpen(true);
              }}
              onShowMap={tileWanted && lowData.on && !mapAsked ? () => setMapAsked(true) : null}
              onHowTo={() => help.show('recovery')}
              now={now}
            />
            {phase === 'idle' ? <FramingHintLine hint={framing} /> : null}
            {hint.text ? (
              <div className="vp-hint" role={hint.alert ? 'alert' : 'status'}>{hint.text}</div>
            ) : null}
            {/* A4: in the bottom stack's flow, under every other line — held sideways nothing can lie over Restart camera */}
            <CameraStalled cam={cam} />
          </div>
          {mapOpen && cardModel.kind === 'fix' && tile.status === 'ready' && tile.ref ? (
            <StampMapSheet fix={cardModel} centre={tile.ref} mosaic={mosaic} distanceWords={distanceWordsFor(t, cardModel.lines)} onClose={() => setMapOpen(false)} />
          ) : null}
        </div>
      )}

      <CaptureBar mode={decide ? 'decide' : 'shoot'} caption={caption} lang={!recording}>
        {recording ? (
          <>
            <BarSlot side="l" />
            <BarSlot side="c">
              <Shutter kind="stop" label={t('sv.action.stopRecording')} onClick={rec.stop} data-testid="stop-recording" />
            </BarSlot>
          </>
        ) : phase === 'rejected' ? (
          <>
            <Button onClick={retake} data-testid="retake"><Icon name="redo" size={18} /> {t('sv.action.reRecord')}</Button>
            {verdict?.mayProceedUnevaluated ? (
              <Button variant="soft" onClick={useAnyway} data-testid="use-anyway">{t('sv.action.useAnyway')}</Button>
            ) : null}
          </>
        ) : phase === 'judging' || phase === 'sealing' || phase === 'sealed' ? (
          <Button disabled>{t('sv.capture.assessing')}</Button>
        ) : camBlocked ? (
          <Button onClick={turnOn}>{t('sv.action.allowed')}</Button>
        ) : cam.phase === 'primer' ? (
          <Button onClick={turnOn} data-testid="turn-on-camera">{t('sv.action.turnOnCamera')}</Button>
        ) : (
          <>
            <BarSlot side="l">
              {cam.multiCamera !== false ? (
                <BarButton icon="flip" label={t('sv.action.flipShort')} aria-label={t('sv.action.flipCamera')} onClick={flip} disabled={!live} data-testid="flip-camera" />
              ) : null}
            </BarSlot>
            <BarSlot side="c">
              <Shutter kind="record" label={t('sv.action.startRecording')} onClick={() => void begin()} disabled={!live || !cam.framesSeen} data-testid="start-recording" />
            </BarSlot>
          </>
        )}
      </CaptureBar>
    </div>
  );
}
